import React, { useEffect, useRef } from 'react';
import { Domain as DomainIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderSupplierCardProps extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const SupplierCardFields = [
  {
    name: 'supplierEvaluationCompleted',
    label: 'Has supplier been evaluated if applicable?'
  },
  { name: 'ndaApplicable', label: 'Is there an NDA in place?' },
  {
    name: 'supplierAgreementNeeded',
    label: 'Is a supplier agreement needed?'
  },
  {
    name: 'termsConditionsImpact',
    label: 'Impact to Terms and Conditions?'
  },
  { name: 'firstArticleRequired', label: 'First Article required?' },
  {
    name: 'inspectionProcedureImpact',
    label: 'Impact to inspection procedures?'
  }
];

export const ChangeOrderSupplierCard: React.FC<
  ChangeOrderSupplierCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    supplierEvaluationCompleted: changeOrder.supplierEvaluationCompleted || '',
    ndaApplicable: changeOrder.ndaApplicable || '',
    supplierAgreementNeeded: changeOrder.supplierAgreementNeeded || '',
    termsConditionsImpact: changeOrder.termsConditionsImpact || '',
    firstArticleRequired: changeOrder.firstArticleRequired || '',
    inspectionProcedureImpact: changeOrder.inspectionProcedureImpact || '',
    commentsSupplier: changeOrder.commentsSupplier || '',
    supplierDueDate: changeOrder.supplierDueDate,
    supplierCompletedDate: changeOrder.supplierCompletedDate
  };
  const commonFieldOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);
  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <DomainIcon />
          Supplier (SQM) Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {SupplierCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={
                  field.name === 'supplierEvaluationCompleted'
                    ? commonFieldOptions.concat({ value: 'n/a', label: 'N/A' })
                    : commonFieldOptions
                }
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='supplierDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('supplierDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='supplierCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('supplierCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsSupplier'
              label='Comments'
              disabled={isFieldDisabled('commentsSupplier')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
